import React from "react";
import moment from "moment";
import { useFormikContext } from "formik";
import { PluginStore } from "graylog-web-plugin/plugin";
import { FormikInput } from "components/common";
import { DATA_TIERING_TYPE } from "components/indices/data-tiering";
const dayFields = ["index_lifetime_max", "index_lifetime_min", "index_hot_lifetime_min"];
const hotWarmOnlyFormFields = ["index_hot_lifetime_min", "warm_tier_enabled", "warm_tier_repository_name"];
const DATA_TIERING_HOT_ONLY_DEFAULTS = {
  index_lifetime_max: 40,
  index_lifetime_min: 30
};
const DATA_TIERING_HOT_WARM_DEFAULTS = {
  archive_before_deletion: false,
  warm_tier_enabled: false,
  index_hot_lifetime_min: 10,
  warm_tier_repository_name: null
};
export const durationToRoundedDays = (duration) => Math.round(moment.duration(duration).asDays());
const dataTieringFormValuesWithDefaults = (values, pluginStore, isDataTieringImmutable) => {
  var _a;
  const dataTieringPlugin = pluginStore.exports("dataTiering").find((plugin) => plugin.type === DATA_TIERING_TYPE.HOT_WARM);
  const dataTieringType = (_a = dataTieringPlugin == null ? void 0 : dataTieringPlugin.type) != null ? _a : DATA_TIERING_TYPE.HOT_ONLY;
  if (dataTieringType === DATA_TIERING_TYPE.HOT_WARM) {
    const hotWarmDefaults = {
      ...DATA_TIERING_HOT_ONLY_DEFAULTS,
      ...DATA_TIERING_HOT_WARM_DEFAULTS,
      ...values,
      ...values.type === DATA_TIERING_TYPE.HOT_ONLY && isDataTieringImmutable ? { index_hot_lifetime_min: null } : {}
    };
    return hotWarmDefaults;
  }
  const hotOnlyDefaults = { ...DATA_TIERING_HOT_ONLY_DEFAULTS, ...values };
  return hotOnlyDefaults;
};
export const prepareDataTieringInitialValues = (config, pluginStore, isDataTieringImmutable) => {
  let formValues = { ...config };
  dayFields.forEach((field) => {
    if (formValues[field]) {
      const numberValue = durationToRoundedDays(formValues[field]);
      formValues = { ...formValues, [field]: numberValue };
    }
  });
  return dataTieringFormValuesWithDefaults(
    formValues,
    pluginStore,
    isDataTieringImmutable
  );
};
export const prepareDataTieringConfig = (formValues, pluginStore, isDataTieringImmutable) => {
  var _a;
  const dataTieringPlugin = pluginStore.exports("dataTiering").find((plugin) => plugin.type === DATA_TIERING_TYPE.HOT_WARM);
  const dataTieringType = (_a = dataTieringPlugin == null ? void 0 : dataTieringPlugin.type) != null ? _a : DATA_TIERING_TYPE.HOT_ONLY;
  let config = dataTieringFormValuesWithDefaults(formValues, pluginStore, isDataTieringImmutable);
  if (dataTieringType === DATA_TIERING_TYPE.HOT_ONLY) {
    hotWarmOnlyFormFields.forEach((field) => {
      delete config[field];
    });
  }
  dayFields.forEach((field) => {
    if (config[field]) {
      config = { ...config, [field]: `P${config[field]}D` };
    }
  });
  return { ...config, type: dataTieringType };
};
const DataTieringConfiguration = ({
  valuesPrefix = void 0,
  hiddenFields = [],
  immutableFields = [],
  ignoreFieldRestrictions
}) => {
  const dataTieringPlugin = PluginStore.exports("dataTiering").find((plugin) => plugin.type === "hot_warm");
  const { values } = useFormikContext();
  const sectionDisabled = immutableFields.includes("data_tiering");
  const formValue = (field) => {
    var _a, _b, _c;
    if (valuesPrefix) {
      return (_b = (_a = values[valuesPrefix]) == null ? void 0 : _a.data_tiering) == null ? void 0 : _b[field];
    }
    return (_c = values == null ? void 0 : values.data_tiering) == null ? void 0 : _c[field];
  };
  const fieldName = (field) => {
    if (valuesPrefix) {
      return `${valuesPrefix}.data_tiering.${field}`;
    }
    return `data_tiering.${field}`;
  };
  const validateMaxDaysInStorage = (value) => {
    if (value < 0) {
      return "Negative numbers are not allowed.";
    }
    return "";
  };
  const validateMinDaysInStorage = (value) => {
    const errors = [];
    if (value < 0) {
      errors.push("Negative numbers are not allowed.");
    }
    if (value > formValue("index_lifetime_max")) {
      errors.push("Min. days in storage needs to be smaller than max. days in storage.");
    }
    if (formValue("warm_tier_enabled") && value < formValue("index_hot_lifetime_min")) {
      errors.push("Min. days in storage needs to be bigger than min. days in hot tier.");
    }
    if (errors.length > 0) {
      return errors.join(" ");
    }
    return "";
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, (!hiddenFields.includes("data_tiering.index_lifetime_max") || ignoreFieldRestrictions) && /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      id: "data-tiering-index-lifetime-max",
      label: "Max. days in storage",
      name: fieldName("index_lifetime_max"),
      min: 0,
      help: "After how many days your data should be deleted.",
      validate: validateMaxDaysInStorage,
      required: true,
      disabled: (immutableFields.includes("data_tiering.index_lifetime_max") || sectionDisabled) && !ignoreFieldRestrictions
    }
  ), (!hiddenFields.includes("data_tiering.index_lifetime_min") || ignoreFieldRestrictions) && /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      id: "data-tiering-index-lifetime-min",
      label: "Min. days in storage",
      name: fieldName("index_lifetime_min"),
      min: 0,
      max: formValue("index_lifetime_max"),
      validate: validateMinDaysInStorage,
      help: "How many days at minimum your data should be stored.",
      required: true,
      disabled: (immutableFields.includes("data_tiering.index_lifetime_min") || sectionDisabled) && !ignoreFieldRestrictions
    }
  ), dataTieringPlugin && /* @__PURE__ */ React.createElement(React.Fragment, null, (!hiddenFields.includes("data_tiering.archive_before_deletion") || ignoreFieldRestrictions) && /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      id: "data_tiering-archive-before-deletion",
      label: "Archive before deletion",
      name: fieldName("archive_before_deletion"),
      help: "Archive this index before it is deleted?",
      disabled: (immutableFields.includes("data_tiering.archive_before_deletion") || sectionDisabled) && !ignoreFieldRestrictions
    }
  ), /* @__PURE__ */ React.createElement(
    dataTieringPlugin.TiersConfigurationFields,
    {
      valuesPrefix,
      hiddenFields,
      immutableFields,
      ignoreFieldRestrictions
    }
  )));
};
export default DataTieringConfiguration;
